import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { getSupabase } from './client';
import { DataError, toDataError } from './errors';

export type SignInFailureReason = 'bad_credentials' | 'rate_limited' | 'network' | 'unknown';

export class SignInError extends Error {
  readonly reason: SignInFailureReason;

  constructor(reason: SignInFailureReason, message: string) {
    super(message);
    this.name = 'SignInError';
    this.reason = reason;
  }
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export async function getSession(): Promise<Session | null> {
  const { data, error } = await getSupabase().auth.getSession();
  if (error) throw toDataError(error);
  return data.session;
}

export async function signInWithPassword(email: string, password: string): Promise<Session> {
  const normalized = normalizeEmail(email);
  if (!isValidEmail(normalized) || password.length === 0) {
    throw new SignInError('bad_credentials', 'Invalid email or password.');
  }

  try {
    const { data, error } = await getSupabase().auth.signInWithPassword({ email: normalized, password });
    if (error) {
      if (error.status === 429 || /rate limit/i.test(error.message)) {
        throw new SignInError('rate_limited', error.message);
      }
      if (error.status === 400 || /invalid login|invalid credentials|email not confirmed/i.test(error.message)) {
        throw new SignInError('bad_credentials', error.message);
      }
      const mapped = toDataError(error);
      throw new SignInError(mapped.code === 'network' ? 'network' : 'unknown', error.message);
    }
    return data.session;
  } catch (err: unknown) {
    if (err instanceof SignInError) throw err;
    const mapped = toDataError(err);
    throw new SignInError(mapped.code === 'network' ? 'network' : 'unknown', mapped.message);
  }
}

/**
 * Starts the Google OAuth redirect. The browser leaves the page; Supabase sends it back to
 * `redirectTo` with the session already in the URL fragment (detectSessionInUrl picks it up),
 * so the caller never gets a session back here directly.
 */
export async function signInWithGoogle(redirectTo: string): Promise<void> {
  const { error } = await getSupabase().auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo },
  });
  if (error) throw toDataError(error);
}

export async function signOut(): Promise<void> {
  // "local" clears this browser only; other devices of the same user stay signed in.
  const { error } = await getSupabase().auth.signOut({ scope: 'local' });
  if (error && !/session.*missing/i.test(error.message)) throw toDataError(error);
}

export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void,
): () => void {
  const { data } = getSupabase().auth.onAuthStateChange(callback);
  return () => data.subscription.unsubscribe();
}

/** Sends a password reset link. The link returns to `redirectTo` with a recovery session. */
export async function sendPasswordResetEmail(email: string, redirectTo: string): Promise<void> {
  const normalized = normalizeEmail(email);
  if (!isValidEmail(normalized)) throw new DataError('invalid', 'Enter a valid email address.');
  const { error } = await getSupabase().auth.resetPasswordForEmail(normalized, { redirectTo });
  if (error) throw toDataError(error);
}

export async function updateOwnPassword(newPassword: string): Promise<void> {
  if (newPassword.length < 8) throw new DataError('invalid', 'Password must be at least 8 characters.');
  const { error } = await getSupabase().auth.updateUser({ password: newPassword });
  if (error) throw toDataError(error);
}

export interface AuthRedirectInfo {
  readonly isRecovery: boolean;
  readonly errorDescription: string | null;
}

/** Inspect the URL fragment before the client consumes it (implicit-flow redirects). */
export function readAuthRedirect(hash: string): AuthRedirectInfo {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return {
    isRecovery: params.get('type') === 'recovery',
    errorDescription: params.get('error_description'),
  };
}
